import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useTaxonomyList } from './useSectorTaxonomy';
import {
  createMapping,
  patchMapping,
  type SectorMappingItem,
} from '../../api/sectorTaxonomyApi';
import { CanonicalSelect } from './CanonicalSelect';

interface Props {
  target: SectorMappingItem;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

interface ExtraTarget {
  canonicalSectorId: string;
  weight: number;
}

// 1:N 편집 모달.
// - 기존 매핑 weight / canonical 수정: PATCH /mappings/{id}
// - 추가 타겟 (같은 source/sourceSectorName + 다른 canonical): POST /mappings
// Proposal §2.5 — UNIQUE(source, source_sector_name) 제약 없음, 1:N 허용.
export function MappingEditModal({
  target,
  onClose,
  onSaved,
  onError,
}: Props): JSX.Element {
  const [canonicalId, setCanonicalId] = useState(target.canonicalSectorId);
  const [weight, setWeight] = useState(target.weight);
  const [extras, setExtras] = useState<ExtraTarget[]>([]);
  const [saving, setSaving] = useState(false);

  // 정규 섹터 목록 (드롭다운용)
  const taxonomy = useTaxonomyList({ isActive: true });

  useEffect(() => {
    setCanonicalId(target.canonicalSectorId);
    setWeight(target.weight);
    setExtras([]);
  }, [target]);

  function addExtra(): void {
    setExtras((prev) => [...prev, { canonicalSectorId: '', weight: 1.0 }]);
  }

  function updateExtra(i: number, patch: Partial<ExtraTarget>): void {
    setExtras((prev) =>
      prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e))
    );
  }

  function removeExtra(i: number): void {
    setExtras((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleSave(): Promise<void> {
    if (saving) return;
    if (!canonicalId.trim()) {
      onError('canonical_sector_id 가 필요합니다.');
      return;
    }
    if (weight < 0 || weight > 1) {
      onError('weight 는 0~1 범위여야 합니다.');
      return;
    }
    for (const ex of extras) {
      if (!ex.canonicalSectorId.trim()) {
        onError('추가 타겟의 canonical_sector_id 를 선택하세요.');
        return;
      }
    }
    setSaving(true);
    try {
      // 기존 매핑 수정
      await patchMapping(target.mappingId, {
        canonicalSectorId: canonicalId.trim(),
        weight,
      });
      // 추가 타겟 POST
      for (const ex of extras) {
        await createMapping({
          source: target.source,
          sourceSectorName: target.sourceSectorName,
          canonicalSectorId: ex.canonicalSectorId.trim(),
          weight: ex.weight,
          status: 'confirmed',
          mappingReason: '운영자 수동 1:N 분기',
        });
      }
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'save_failed';
      onError(`저장 실패: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  const options = taxonomy.data ?? [];

  return (
    <div className="tx-modal-backdrop" role="dialog" aria-modal="true">
      <div className="tx-modal" style={{ maxWidth: 640 }}>
        <h3 className="tx-modal__title">매핑 편집 (1:N)</h3>
        <div className="tx-modal__body">
          <div>
            <strong>{target.source}</strong> / {target.sourceSectorName}
          </div>
          <div style={{ marginTop: 8 }} className="pca-muted">
            mapping_id {target.mappingId} · 이 원천 섹터를 여러 정규 섹터로
            분기하려면 아래 “타겟 추가” 를 사용하세요.
          </div>
        </div>

        <div className="pca-filter" style={{ marginBottom: 0 }}>
          <div className="pca-filter__group" style={{ flex: 1 }}>
            <label className="pca-filter__label" htmlFor="mapping-edit-canonical">
              기존 canonical
            </label>
            <CanonicalSelect
              inputId="mapping-edit-canonical"
              ariaLabel="기존 canonical"
              value={canonicalId}
              onChange={setCanonicalId}
              options={options}
              excludeMetaL1
            />
          </div>
          <div className="pca-filter__group">
            <label className="pca-filter__label">weight</label>
            <input
              type="number"
              className="pca-input"
              step={0.05}
              min={0}
              max={1}
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
              style={{ width: 90 }}
            />
          </div>
        </div>

        {extras.map((ex, i) => (
          <div key={i} className="pca-filter" style={{ marginTop: 6, marginBottom: 0 }}>
            <div className="pca-filter__group" style={{ flex: 1 }}>
              <label
                className="pca-filter__label"
                htmlFor={`mapping-edit-extra-${i}`}
              >
                추가 canonical #{i + 1}
              </label>
              <CanonicalSelect
                inputId={`mapping-edit-extra-${i}`}
                ariaLabel={`추가 canonical #${i + 1}`}
                value={ex.canonicalSectorId}
                onChange={(v) => updateExtra(i, { canonicalSectorId: v })}
                options={options}
                excludeMetaL1
              />
            </div>
            <div className="pca-filter__group">
              <label className="pca-filter__label">weight</label>
              <input
                type="number"
                className="pca-input"
                step={0.05}
                min={0}
                max={1}
                value={ex.weight}
                onChange={(e) => updateExtra(i, { weight: Number(e.target.value) })}
                style={{ width: 90 }}
              />
            </div>
            <div className="pca-filter__group" style={{ marginLeft: 'auto' }}>
              <label className="pca-filter__label">&nbsp;</label>
              <button
                type="button"
                className="pca-btn"
                onClick={() => removeExtra(i)}
                aria-label="remove extra target"
              >
                <Trash2 size={12} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        ))}

        <div className="tx-modal__actions">
          <button type="button" className="pca-btn" onClick={addExtra}>
            <Plus size={12} strokeWidth={1.75} />
            타겟 추가
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="pca-btn" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="pca-btn pca-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving && <Loader2 size={12} strokeWidth={1.75} className="pca-spin" />}
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
